/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEnhanceSpeechLimits } from "components/EnhancedSpeech/useEnhanceSpeechLimits";
import { useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  EnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
  getIntervalDescription,
  userMessageForEnhanceTrackError,
} from "components/EnhancedSpeech/Utilities";
import { addToast } from "actions/ToastMessageActions";
import { UseEnhanceDispatch } from "./Reducer";
import { useBuildEnhancement } from "./useBuildEnhancement";
import { useCurrentUser } from "actions/CurrentUserActions";
import Analytics, { AnalyticsEvent } from "constants/Analytics";

export const useErrorHandler = (
  localDispatch: UseEnhanceDispatch
): ((track: EnhanceSpeechTrack) => void) => {
  const { currentUser } = useCurrentUser();
  const userLimits = useEnhanceSpeechLimits();
  const buildEnhancement = useBuildEnhancement(localDispatch);
  const dispatch = useDispatch();
  return useCallback(
    (track: EnhanceSpeechTrack): void => {
      // Premium users keep enhancements with errors
      // TODO: Are there any that we actually show a toast for and not put
      // in the list?
      if (currentUser?.hasPremiumAccess) {
        localDispatch({
          type: "UPDATE_ENHANCEMENT",
          enhancement: buildEnhancement(track),
        });
        return;
      }

      switch (track.errorType) {
        case EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_TIMEOUT_EXCEPTION:
        case EnhanceSpeechTrackErrorType.PROCESSING_TIMEOUT:
        case EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_UNSUPPORTED_FORMAT_EXCEPTION:
        case EnhanceSpeechTrackErrorType.UNSUPPORTED_FORMAT:
        case EnhanceSpeechTrackErrorType.INCALCULABLE_DURATION:
        case EnhanceSpeechTrackErrorType.RATE_LIMIT:
        case EnhanceSpeechTrackErrorType.UPLOAD_FAILED:
        case EnhanceSpeechTrackErrorType.UNSUPPORTED_EXTENSION:
        case EnhanceSpeechTrackErrorType.COLLIGO_SILENT_INPUT:
          dispatch(
            addToast({
              message: userMessageForEnhanceTrackError(
                track.errorType,
                userLimits,
                getIntervalDescription(track.limits?.retryAtTime)
              ),
            })
          );
          localDispatch({
            type: "REMOVE_ENHANCEMENT",
            trackId: track.id,
          });
          break;
        case EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED:
          Analytics.instance.logEvent(
            AnalyticsEvent.USER_TRIGGERS_VIDEO_UPSELL
          );
        // Don't break here, we want to continue on below to send UPDATE_ENHANCEMENT
        case EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT:
        case EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT:
        case EnhanceSpeechTrackErrorType.CONCURRENCY_LIMIT:
        case EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT:
          localDispatch({
            type: "UPDATE_ENHANCEMENT",
            enhancement: buildEnhancement(track),
          });
          break;
        default:
          const error = new Error(
            `Enhance failed for track with unhandled errorType: ${track.errorType}`
          );
          newrelic.noticeError(error, {
            trackId: track.id,
          });

          dispatch(
            addToast({
              message: I18n.t("enhance_speech.generic_failure"),
            })
          );

          localDispatch({
            type: "REMOVE_ENHANCEMENT",
            trackId: track.id,
          });
      }
    },
    [
      localDispatch,
      buildEnhancement,
      dispatch,
      userLimits,
      currentUser?.hasPremiumAccess,
    ]
  );
};
